export interface ExamplePromptAttachment {
  url: string;
  filename: string;
  contentType: string;
}

export interface ExamplePrompt {
  id: string;
  defaultMessage: string;
  promptId?: string;
  promptDefaultMessage?: string;
  attachments?: ExamplePromptAttachment[];
}

const ENGINEERING_DRAWING_PROMPT =
  'I am attaching a scanned 2D engineering drawing (image) of a legacy aerospace part — '
  + 'multi-view with dimensioned GD&T. Interpret the views, infer the projection standard '
  + '(state assumption if unmarked), reconstruct a parametric STEP model that honours every '
  + 'called-out dimension and tolerance, and flag any dimension that was ambiguous. '
  + 'Persona: Aerospace Design Engineer at OEM (PLM context). '
  + 'Verification: bounding box vs drawing, hole diameter check, mass comparison. '
  + 'Success: every drawing dimension preserved within 0.1 mm. '
  + "Optional next step: ask if I'd like to use CAM or 3D printing or FEA and explain "
  + 'which tools are available for either.';

export const EXAMPLE_PROMPTS: readonly ExamplePrompt[] = [
  {
    id: 'taskPrompt.examples.topologyBracket',
    defaultMessage: 'Generate an image of a topology optimised bracket',
  },
  {
    id: 'taskPrompt.examples.tellMeWhatYouCanDo',
    defaultMessage: 'Tell me what you can do',
  },
  {
    id: 'taskPrompt.examples.droneMesh',
    defaultMessage: 'Make a 3D mesh of a drone',
  },
  {
    id: 'taskPrompt.examples.processEditor',
    defaultMessage: 'What is the process editor?',
  },
  {
    id: 'taskPrompt.examples.engineeringDrawing.chip',
    defaultMessage: 'Turn engineering drawing into CAD',
    promptId: 'taskPrompt.examples.engineeringDrawing.prompt',
    promptDefaultMessage: ENGINEERING_DRAWING_PROMPT,
    attachments: [
      {
        url: '/example-prompts/drawing-sun-shaft.jpg',
        filename: 'drawing-sun-shaft.jpg',
        contentType: 'image/jpeg',
      },
    ],
  },
];

export const loadExampleAttachments = async (
  attachments: readonly ExamplePromptAttachment[],
): Promise<File[]> =>
  Promise.all(
    attachments.map(async (a) => {
      const res = await fetch(a.url);
      if (!res.ok) {
        throw new Error(
          `Failed to load example attachment ${a.filename} (HTTP ${res.status})`,
        );
      }
      const blob = await res.blob();
      return new File([blob], a.filename, { type: a.contentType });
    }),
  );
